import {
  APPROVAL_STEPS,
  type ApprovalStep,
  approvalStepPerms,
  CONTRACT_KINDS,
  CONTRACT_STATUSES,
  type ContractDetail,
  type ContractKind,
  type ContractStatus,
  type ContractSummary,
  can,
  canAnywhere,
  PAYMENT_BASES,
  seesMoney,
  VAT_STATUSES,
  type VatStatus,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { useCostArticles } from '../../lib/articles';
import { date, dateTime, tenge, todayLocal } from '../../lib/format';
import { counterpartiesQuery, projectQuery, projectsQuery } from '../../lib/projects';
import { useCan, useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, type Tone, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { AdvancesView } from '../finance/Advances';
import { NewPaymentRequestDialog, PaymentRequestsTable, paymentRequestsQuery } from '../procurement/payments';
import { SplitBox } from '../procurement/split';
import { AmendmentsPanel, VersionChanges } from './amendments';
import { AdvanceGuaranteeToggle, ContractDocsPanel, DeliveryState, ScheduleCoverage } from './contractDocs';
import { fromScheduleDraft, type ScheduleDraftRow, ScheduleRowsEditor, toScheduleDraft } from './editors';
import { ContractExecutionPanel, PenaltyTerms } from './execution';
import { ContractObligationsPanel, ObligationsSchedulePanel } from './obligations';
import { ContractDeliveriesPanel, CustomerMaterialsPanel } from './warehouseLinks';
import { ContractWarrantyPanel } from './warranty';

const STATUS_TONE: Record<ContractStatus, Tone> = {
  draft: 'neutral',
  approval: 'info',
  approved: 'success',
  signed: 'ink',
  completed: 'success',
  terminated: 'danger',
};

const fmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

export function ContractsTable({ projectId }: { projectId?: string }) {
  const q = useQuery({
    queryKey: ['contracts', projectId ?? 'all'],
    queryFn: () => api<ContractSummary[]>('/contracts', { query: { projectId } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data.length)
    return (
      <EmptyState title="Договоров пока нет">
        Расходный договор создаётся из заявки на закуп после выбора поставщика: спецификация и условия
        подставятся сами.
      </EmptyState>
    );
  return (
    <Panel bodyClassName="p-0">
      <ScrollX>
        <table className="w-full min-w-[960px] text-[14px]">
          <thead>
            <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
              <th className="py-2.5 pr-3 pl-5 font-medium">Договор</th>
              <th className="py-2.5 pr-3 font-medium">Контрагент</th>
              <th className="py-2.5 pr-3 font-medium">Проект</th>
              <th className="py-2.5 pr-3 text-right font-medium">Сумма</th>
              <th className="py-2.5 pr-3 font-medium">Согласование</th>
              <th className="py-2.5 pr-5 font-medium">Статус</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((c) => (
              <tr key={c.id} className="border-t border-line align-top">
                <td className="py-2.5 pr-3 pl-5">
                  <Link to={`/contracts/${c.id}`} className="font-medium text-ink hover:underline">
                    {c.number}
                  </Link>
                  <div className="text-[13px] text-ink-2">
                    {CONTRACT_KINDS[c.kind]}: {c.subject}
                  </div>
                </td>
                <td className="py-2.5 pr-3">{c.counterpartyName}</td>
                <td className="py-2.5 pr-3 text-ink-2">{c.projectCode}</td>
                <td className="num py-2.5 pr-3 text-right whitespace-nowrap">{tenge(c.amount)}</td>
                <td className="num py-2.5 pr-3 text-ink-2">
                  {c.approvalsTotal ? `${c.approvalsDone} из ${c.approvalsTotal}` : '—'}
                  {c.approvalsOverdue ? (
                    <span className="ml-1 text-danger">просрочено {c.approvalsOverdue}</span>
                  ) : null}
                </td>
                <td className="py-2.5 pr-5">
                  <Badge tone={c.signedBypass ? 'danger' : STATUS_TONE[c.status]}>
                    {c.signedBypass ? 'подписан в обход маршрута' : CONTRACT_STATUSES[c.status]}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollX>
    </Panel>
  );
}

/**
 * Новый договор без заявки на закуп: доходный с заказчиком (ПФ-24, ДГ-19) или расходный — услуги,
 * аренда, подряд. Договор из выбранного КП создаётся в самой заявке.
 */
function NewContractDialog({ onClose }: { onClose: () => void }) {
  const costArticles = useCostArticles();
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const projects = useQuery(projectsQuery('active'));
  const cps = useQuery(counterpartiesQuery());
  const [f, setF] = useState({
    projectId: '',
    kind: 'revenue' as ContractKind,
    counterpartyId: '',
    number: '',
    subject: '',
    amount: '',
    vatStatus: 'incl_vat' as VatStatus,
    startDate: '',
    endDate: '',
    costArticle: '',
  });
  const allowed = (projects.data ?? []).filter((p) =>
    can(me.access, 'contracts.edit', {
      legalEntityId: p.legalEntityId,
      directionId: p.directionId,
      projectId: p.id,
      projectPath: [p.parentId],
    }),
  );
  const revenue = f.kind === 'revenue';
  const parties = (cps.data ?? []).filter((c) =>
    revenue ? c.isCustomer : c.isSupplier || c.isContractor || c.isDesigner,
  );
  const amount = Number(f.amount.replace(/\s/g, '').replace(',', '.'));
  const m = useMutation({
    mutationFn: () =>
      api<ContractDetail>('/contracts', {
        body: {
          projectId: f.projectId,
          counterpartyId: f.counterpartyId,
          kind: f.kind,
          number: f.number.trim() || undefined,
          subject: f.subject.trim(),
          amount: Number.isFinite(amount) ? amount : 0,
          vatStatus: f.vatStatus,
          startDate: f.startDate || null,
          endDate: f.endDate || null,
          costArticle: revenue ? null : f.costArticle || null,
        },
      }),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      toast(`Создан проект договора ${c.number}`);
      onClose();
      navigate({ to: '/contracts/$contractId', params: { contractId: c.id } });
    },
  });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Новый договор"
      description="Доходный договор с заказчиком или расходный без заявки на закуп. Договор по выбранному КП создаётся в заявке."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={
              !f.projectId ||
              !f.counterpartyId ||
              f.subject.trim().length < 3 ||
              !Number.isFinite(amount) ||
              amount < 0
            }
            onClick={() => m.mutate()}
          >
            Создать проект договора
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Проект" className="sm:col-span-2">
          <Select value={f.projectId} onChange={(e) => set('projectId', e.target.value)}>
            <option value="">— выберите —</option>
            {allowed.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Вид договора">
          <Select
            value={f.kind}
            onChange={(e) =>
              setF((x) => ({ ...x, kind: e.target.value as ContractKind, counterpartyId: '' }))
            }
          >
            {(Object.keys(CONTRACT_KINDS) as ContractKind[]).map((k) => (
              <option key={k} value={k}>
                {CONTRACT_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={revenue ? 'Заказчик' : 'Контрагент'}>
          <Select value={f.counterpartyId} onChange={(e) => set('counterpartyId', e.target.value)}>
            <option value="">— выберите —</option>
            {parties.map((c) => (
              <option key={c.id} value={c.id}>
                {c.shortName || c.name}
                {c.bin ? `, БИН ${c.bin}` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Номер" hint="Пусто — номер выдаст портал">
          <Input
            value={f.number}
            onChange={(e) => set('number', e.target.value)}
            placeholder="227-23-П-УВПиОС"
          />
        </Field>
        <Field label="Сумма, ₸">
          <Input inputMode="decimal" value={f.amount} onChange={(e) => set('amount', e.target.value)} />
        </Field>
        <Field label="Предмет договора" className="sm:col-span-2">
          <Textarea rows={2} value={f.subject} onChange={(e) => set('subject', e.target.value)} />
        </Field>
        <Field label="НДС в цене">
          <Select value={f.vatStatus} onChange={(e) => set('vatStatus', e.target.value)}>
            {(Object.keys(VAT_STATUSES) as VatStatus[]).map((k) => (
              <option key={k} value={k}>
                {VAT_STATUSES[k]}
              </option>
            ))}
          </Select>
        </Field>
        {revenue ? null : (
          <Field label="Статья ФП">
            <Select value={f.costArticle} onChange={(e) => set('costArticle', e.target.value)}>
              <option value="">— выберите —</option>
              {costArticles.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Начало">
          <Input type="date" value={f.startDate} onChange={(e) => set('startDate', e.target.value)} />
        </Field>
        <Field label="Окончание">
          <Input type="date" value={f.endDate} onChange={(e) => set('endDate', e.target.value)} />
        </Field>
      </div>
      {revenue ? (
        <p className="mt-3 text-[13px] text-ink-2">
          Подписанный после маршрута согласования доходный договор с суммой открывает проект для заявок и
          расходных договоров (ПФ-23).
        </p>
      ) : null}
      <ErrorBox error={m.error} className="mt-3" />
    </Dialog>
  );
}

export function ContractsPage() {
  const me = useMe();
  const [creating, setCreating] = useState(false);
  return (
    <Page>
      <PageHeader
        title="Договоры"
        subtitle="Расходные и доходные договоры: маршрут из пяти согласующих, подписание только после маршрута, карточка договора и 1С"
        actions={
          canAnywhere(me.access, 'contracts.edit') ? (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Новый договор
            </Button>
          ) : null
        }
      />
      <ObligationsSchedulePanel />
      <ContractsTable />
      {creating ? <NewContractDialog onClose={() => setCreating(false)} /> : null}
    </Page>
  );
}

function ScheduleEditor({ d, onSaved }: { d: ContractDetail; onSaved: (x: ContractDetail) => void }) {
  const me = useMe();
  // Без доступа к суммам платежи видны без сумм: их меняет тот, кто видит суммы, здесь — только поставки
  const money = seesMoney(me.access, 'acts_invoices');
  const payments = d.schedule.filter((r) => r.kind === 'payment');
  const [rows, setRows] = useState<ScheduleDraftRow[]>(
    toScheduleDraft(money ? d.schedule : d.schedule.filter((r) => r.kind === 'delivery')),
  );
  const save = useMutation({
    mutationFn: () =>
      api<ContractDetail>(`/contracts/${d.id}/schedule`, {
        method: 'PUT',
        body: { rows: fromScheduleDraft(rows) },
      }),
    onSuccess: (x) => {
      onSaved(x);
      toast('Графики сохранены');
    },
  });
  return (
    <div className="flex flex-col gap-2">
      {!money && payments.length ? (
        <div className="rounded-md bg-surface-2 px-3 py-2 text-[13px] text-ink">
          <p className="text-ink-2">
            Платежи ({payments.length}) меняет сотрудник с доступом к суммам договоров; здесь — график
            поставок.
          </p>
          <ul className="mt-1 list-disc pl-5">
            {payments.map((r) => (
              <li key={r.id}>
                {r.description || (r.basis ? PAYMENT_BASES[r.basis] : 'Платёж')}
                {r.plannedDate ? `, ${date(r.plannedDate)}` : ''}
                {r.percent != null ? `, ${r.percent} %` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <ScheduleRowsEditor
        rows={rows}
        onChange={setRows}
        kinds={money ? undefined : ['delivery']}
        items={d.items}
      />
      <div>
        <Button size="sm" variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
          {money ? 'Сохранить графики' : 'Сохранить график поставок'}
        </Button>
      </div>
      <ErrorBox error={save.error} />
    </div>
  );
}

function ContractPayments({ contractId, requestId }: { contractId: string; requestId: string | null }) {
  const q = useQuery(paymentRequestsQuery({ contractId }));
  const me = useMe();
  // Заявку на оплату с суммой готовит тот, кто видит суммы (ДС-04)
  const canRequest = useCan('procurement.payment.request') && seesMoney(me.access, 'acts_invoices');
  const [creating, setCreating] = useState(false);
  return (
    <Panel
      title="Заявки на оплату по договору"
      meta={requestId ? 'по заявке на закуп они создаются после включения в реестр' : undefined}
      bodyClassName="p-0"
      actions={
        canRequest && !requestId ? <Button onClick={() => setCreating(true)}>Заявка на оплату</Button> : null
      }
    >
      {q.isPending ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : q.isError ? (
        <ErrorBox error={q.error} className="m-5" />
      ) : (
        <PaymentRequestsTable list={q.data} showSource={false} />
      )}
      {creating ? (
        <NewPaymentRequestDialog contractId={contractId} onClose={() => setCreating(false)} />
      ) : null}
    </Panel>
  );
}

/** ЗК-30: пояснение к выбору по каждому договору — без него договор не уходит на согласование */
function SelectionReasonEditor({ d, onSaved }: { d: ContractDetail; onSaved: (x: ContractDetail) => void }) {
  const me = useMe();
  const [text, setText] = useState(d.selectionReason ?? '');
  // Текст с суммами без доступа к ним виден с маской и только для чтения (ДС-04)
  const locked = !seesMoney(me.access, 'acts_invoices') && (d.selectionReason ?? '').includes('сумма скрыта');
  const save = useMutation({
    mutationFn: () =>
      api<ContractDetail>(`/contracts/${d.id}`, {
        method: 'PATCH',
        body: { selectionReason: text.trim() || null },
      }),
    onSuccess: (x) => {
      onSaved(x);
      toast('Пояснение сохранено');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <Panel title="Почему выбраны этот контрагент и эта цена" meta="приказ № 31П, п. 2">
      <Textarea
        rows={3}
        value={text}
        readOnly={locked}
        placeholder="Например: единственный поставщик с наличием на складе в Атырау, цена ниже второго КП на 4 %"
        onChange={(e) => setText(e.target.value)}
      />
      {locked ? (
        <p className="mt-1 text-[12px] text-ink-2">
          В пояснении есть суммы: изменить его может сотрудник с доступом к суммам договоров.
        </p>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          variant="secondary"
          disabled={text.trim() === (d.selectionReason ?? '').trim()}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Сохранить пояснение
        </Button>
        <span className="text-[12px] text-ink-2">
          Без пояснения договор не уходит на согласование. Если цена выше рыночной, назовите причину.
        </span>
      </div>
    </Panel>
  );
}

export function ContractPage() {
  const { contractId } = useParams({ strict: false }) as { contractId: string };
  const me = useMe();
  const qc = useQueryClient();
  const key = ['contracts', 'detail', contractId];
  // Вернулись на вкладку — карточка обновляется: решения по маршруту принимаются по свежему раунду
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<ContractDetail>(`/contracts/${contractId}`),
    refetchOnWindowFocus: true,
  });
  const project = useQuery({ ...projectQuery(q.data?.projectId ?? ''), enabled: !!q.data });
  useCrumbs(
    q.data
      ? [
          { label: q.data.projectCode, to: `/projects/${q.data.projectId}/contracts` },
          {
            label: q.data.number,
            title: `${CONTRACT_KINDS[q.data.kind]} ${q.data.number}: ${q.data.subject}`,
          },
        ]
      : null,
    q.error,
  );
  const [comment, setComment] = useState('');
  const [signDate, setSignDate] = useState(todayLocal());
  const [oneC, setOneC] = useState({ number: '', registeredAt: todayLocal() });
  const done = (x: ContractDetail) => {
    qc.setQueryData(key, x);
    qc.invalidateQueries({ queryKey: ['contracts'] });
    qc.invalidateQueries({ queryKey: ['attention'] });
  };
  const act = useMutation({
    mutationFn: (p: { path: string; body?: unknown }) =>
      api<ContractDetail>(`/contracts/${contractId}${p.path}`, { method: 'POST', body: p.body }),
    onSuccess: (x) => {
      done(x);
      toast('Готово');
      setComment('');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const d = q.data;
  const p = project.data;
  const target = p
    ? {
        legalEntityId: p.legalEntityId,
        directionId: p.directionId,
        projectId: p.id,
        projectPath: [p.parentId],
      }
    : {};
  const canEdit = can(me.access, 'contracts.edit', target);
  const canSign = can(me.access, 'contracts.sign', target) || can(me.access, 'contracts.docs', target);
  const money = seesMoney(me.access, 'acts_invoices');
  const round = Math.max(0, ...d.approvals.map((a) => a.round));
  const current = d.approvals.filter((a) => a.round === round);
  // Кнопки решения — только на пунктах, которые этот человек вправе решить; в раунде один пункт на человека
  const isOwner = Boolean(me.tenant?.isOwner);
  const decidedHere = current.some((a) => a.decidedById === me.user.id);
  // Каждый согласует свой пункт (ДГ-04); автор проекта — только пункт инициатора
  const mayDecide = (step: ApprovalStep) =>
    d.status === 'approval' &&
    (!decidedHere || isOwner) &&
    (step === 'initiator' || d.createdById !== me.user.id || isOwner) &&
    approvalStepPerms(step).some((perm) => can(me.access, perm, target));
  const anyDecision = current.some((a) => a.status === 'pending' && mayDecide(a.step));
  const canConfirmCard = can(me.access, 'project.edit', target);
  const canOneC =
    can(me.access, 'finance.edit', target) ||
    can(me.access, 'contracts.docs', target) ||
    can(me.access, 'procurement.payment.request', target);

  return (
    <Page>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.01em] text-ink">
          {CONTRACT_KINDS[d.kind]} {d.number}
        </h1>
        <Badge tone={d.signedBypass ? 'danger' : STATUS_TONE[d.status]}>
          {d.signedBypass ? 'подписан в обход маршрута' : CONTRACT_STATUSES[d.status]}
        </Badge>
      </div>
      <p className="mt-1 text-[14px] text-ink-2">
        {d.counterpartyName} · {d.subject} ·{' '}
        {d.amount !== null ? `${tenge(d.amount)} ${VAT_STATUSES[d.vatStatus]}` : 'сумма скрыта'}
        {d.signedDate ? ` · подписан ${date(d.signedDate)}` : ''}
        {d.requestId ? (
          <>
            {' · по заявке '}
            <Link to={`/procurement/${d.requestId}`} className="text-ink underline-offset-4 hover:underline">
              {d.requestNumber}
            </Link>
          </>
        ) : null}
      </p>

      {d.split ? (
        <div className="mt-4 max-w-3xl">
          <SplitBox splits={[d.split]} />
        </div>
      ) : null}

      {d.warnings.length ? (
        <div className="mt-4 rounded-md bg-warning-soft px-4 py-3 text-[14px] text-ink">
          <ul className="list-disc pl-5">
            {d.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex flex-col gap-6">
          <Panel
            title="Спецификация"
            meta={d.itemsTotal !== null ? tenge(d.itemsTotal) : undefined}
            bodyClassName="p-0"
          >
            {/* На телефоне — карточки: цена и сумма видны без прокрутки вбок */}
            <ul className="divide-y divide-line md:hidden">
              {d.items.map((i) => (
                <li key={i.id} className="flex flex-col gap-1 px-4 py-3 text-[13px]">
                  <span className="text-[14px] text-ink">{i.name}</span>
                  <span className="num flex flex-wrap justify-between gap-x-3 text-ink-2">
                    <span>
                      {fmt.format(i.quantity)} {i.unit}
                      {i.amount !== null ? ` × ${fmt.format(i.price)}` : ''}
                    </span>
                    {i.amount !== null ? <span className="text-ink">{tenge(i.amount)}</span> : null}
                  </span>
                  {i.estimatePrice !== null ? (
                    <span
                      className={clsx(
                        'num text-[12px]',
                        i.price > i.estimatePrice ? 'text-danger' : 'text-ink-2',
                      )}
                    >
                      смета {fmt.format(i.estimatePrice)}
                      {i.price > i.estimatePrice ? ' — цена выше сметы' : ''}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
            <ScrollX className="hidden md:block">
              <table className="w-full min-w-[640px] text-[13px]">
                <thead>
                  <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                    <th className="py-2 pr-3 pl-5 font-medium">Позиция</th>
                    <th className="py-2 pr-3 text-right font-medium">Кол-во</th>
                    <th className="py-2 pr-3 text-right font-medium">Цена</th>
                    <th className="py-2 pr-3 text-right font-medium">Смета</th>
                    <th className="py-2 pr-5 text-right font-medium">Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((i) => (
                    <tr key={i.id} className="border-t border-line">
                      <td className="py-2 pr-3 pl-5">{i.name}</td>
                      <td className="num py-2 pr-3 text-right whitespace-nowrap">
                        {fmt.format(i.quantity)} {i.unit}
                      </td>
                      <td
                        className={clsx(
                          'num py-2 pr-3 text-right',
                          i.estimatePrice !== null && i.price > i.estimatePrice && 'text-danger',
                        )}
                      >
                        {fmt.format(i.price)}
                      </td>
                      <td className="num py-2 pr-3 text-right text-ink-2">
                        {i.estimatePrice !== null ? fmt.format(i.estimatePrice) : '—'}
                      </td>
                      <td className="num py-2 pr-5 text-right">{tenge(i.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          </Panel>
          <Panel
            title="Графики платежей и поставок"
            meta={d.paymentsTotal !== null ? `платежи ${tenge(d.paymentsTotal)}` : undefined}
          >
            <div className="mb-3">
              <PenaltyTerms d={d} canEdit={canEdit} onSaved={done} />
            </div>
            <div className="mb-3">
              <AdvanceGuaranteeToggle
                d={d}
                canDraft={canEdit || can(me.access, 'finance.manager', target)}
                canDecide={
                  Boolean(me.tenant?.isOwner) ||
                  can(me.access, 'finance.manager', { legalEntityId: target.legalEntityId })
                }
                onSaved={done}
              />
            </div>
            {canEdit && d.status === 'draft' ? (
              <ScheduleEditor d={d} onSaved={done} />
            ) : d.schedule.length ? (
              <ul className="divide-y divide-line">
                {d.schedule.map((r) => (
                  <li key={r.id} className="flex items-start justify-between gap-3 py-2 text-[14px]">
                    <div className="min-w-0">
                      <div className="text-ink">
                        {r.description ||
                          (r.basis ? PAYMENT_BASES[r.basis] : r.kind === 'payment' ? 'Платёж' : 'Поставка')}
                      </div>
                      <div className="text-[12px] text-ink-2">
                        {r.kind === 'payment' ? 'платёж' : 'поставка'}
                        {r.plannedDate ? `, ${date(r.plannedDate)}` : ''}
                        {r.basis === 'invoice_plus_days' && r.days != null
                          ? `, через ${r.days} дн. после накладной`
                          : ''}
                        {r.percent != null ? `, ${r.percent} %` : ''}
                      </div>
                      <ScheduleCoverage r={r} />
                      <DeliveryState r={r} d={d} today={todayLocal()} />
                    </div>
                    <span className="num shrink-0 text-right">
                      {r.kind === 'payment' ? tenge(r.amount) : r.quantity}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[14px] text-ink-2">Графиков нет.</p>
            )}
          </Panel>
          {['signed', 'completed', 'terminated'].includes(d.status) || d.amendments.length ? (
            <AmendmentsPanel d={d} target={target} onSaved={done} />
          ) : null}
          {d.direction === 'expense' && ['signed', 'completed'].includes(d.status) ? (
            <ContractPayments contractId={d.id} requestId={d.requestId} />
          ) : null}
          {/* Авансы и гарантийные удержания (ФН-06): реестр по договору */}
          {['signed', 'completed', 'terminated'].includes(d.status) && money ? (
            <AdvancesView contractId={d.id} />
          ) : null}
          {/* Исполнение: поставлено и оплачено, просрочки и неустойка (ДГ-07, ДГ-13) */}
          {d.direction === 'expense' && ['signed', 'completed'].includes(d.status) ? (
            <ContractExecutionPanel contractId={d.id} />
          ) : null}
          {/* Склад (поток 5): поставки по расходному договору и материалы заказчика по доходному */}
          {d.direction === 'expense' && ['signed', 'completed', 'terminated'].includes(d.status) ? (
            <ContractDeliveriesPanel contractId={d.id} />
          ) : null}
          {d.direction === 'revenue' && ['signed', 'completed', 'terminated'].includes(d.status) ? (
            <CustomerMaterialsPanel counterpartyId={d.counterpartyId} projectId={d.projectId} />
          ) : null}
          {/* Сроки, уведомления и обеспечения (ДГ-20, ДГ-10, ФН-07) */}
          {['signed', 'completed', 'terminated'].includes(d.status) ? (
            <ContractObligationsPanel contractId={d.id} canEdit={canEdit && d.status !== 'terminated'} />
          ) : null}
          {/* Гарантийные обращения заказчика и оценки (ДГ-22, ДГ-27, КА-15) */}
          {['signed', 'completed', 'terminated'].includes(d.status) ? (
            <ContractWarrantyPanel
              contractId={d.id}
              direction={d.direction === 'revenue' ? 'revenue' : 'expense'}
            />
          ) : null}
          {['signed', 'completed', 'terminated'].includes(d.status) ? (
            <ContractDocsPanel d={d} canUpload={canEdit || canSign} />
          ) : null}
          {d.direction === 'expense' && d.status === 'draft' && canEdit ? (
            <SelectionReasonEditor d={d} onSaved={done} />
          ) : d.selectionReason ? (
            <Panel title="Почему выбраны этот контрагент и эта цена">
              <p className="text-[14px] text-ink">{d.selectionReason}</p>
            </Panel>
          ) : null}
          {d.securityMemo ? (
            <Panel title="Служебная записка учредителям">
              <p className="text-[14px] text-ink">{d.securityMemo}</p>
            </Panel>
          ) : null}
        </div>

        <div className="flex flex-col gap-6">
          <Panel title="Маршрут согласования" meta="п. 6.1 Регламента № 33П">
            <VersionChanges d={d} />
            {current.length ? (
              <ul className="divide-y divide-line">
                {current.map((a) => (
                  <li key={a.id} className="py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[14px] font-medium text-ink">{APPROVAL_STEPS[a.step].title}</p>
                        <p className="text-[12px] text-ink-2">{APPROVAL_STEPS[a.step].scope}</p>
                      </div>
                      <Badge
                        tone={
                          a.status === 'approved'
                            ? 'success'
                            : a.status === 'pending'
                              ? a.overdue && d.status === 'approval'
                                ? 'danger'
                                : 'neutral'
                              : 'warning'
                        }
                      >
                        {a.status === 'approved'
                          ? 'согласовано'
                          : a.status === 'pending'
                            ? d.status !== 'approval'
                              ? 'не рассмотрено'
                              : a.overdue
                                ? 'просрочено'
                                : `до ${date(a.dueAt)}`
                            : a.status === 'rejected'
                              ? 'отклонено'
                              : 'на доработку'}
                      </Badge>
                    </div>
                    {a.decidedAt ? (
                      <p className="mt-1 text-[12px] text-ink-2">
                        {a.approverName}, {dateTime(a.decidedAt)}
                        {a.comment ? ` — ${a.comment}` : ''}
                      </p>
                    ) : null}
                    {a.status === 'pending' && mayDecide(a.step) ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="primary"
                          loading={act.isPending}
                          onClick={() =>
                            act.mutate({ path: `/approvals/${a.id}`, body: { decision: 'approved' } })
                          }
                        >
                          Согласовать
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!comment.trim()}
                          onClick={() =>
                            act.mutate({
                              path: `/approvals/${a.id}`,
                              body: { decision: 'returned', comment },
                            })
                          }
                        >
                          На доработку
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={!comment.trim()}
                          onClick={() =>
                            act.mutate({
                              path: `/approvals/${a.id}`,
                              body: { decision: 'rejected', comment },
                            })
                          }
                        >
                          Отклонить
                        </Button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[14px] text-ink-2">Маршрут ещё не запускался.</p>
            )}
            {d.status === 'approval' && decidedHere && !isOwner ? (
              <p className="mt-3 text-[13px] text-ink-2">
                Вы уже приняли решение в этом раунде: остальные пункты согласуют другие люди.
              </p>
            ) : null}
            {anyDecision ? (
              <Textarea
                className="mt-3"
                rows={2}
                placeholder="Комментарий для доработки или отказа"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            ) : null}
            {d.status === 'draft' && canEdit ? (
              <Button
                className="mt-3"
                variant="primary"
                loading={act.isPending}
                onClick={() => act.mutate({ path: '/submit' })}
              >
                Отправить на согласование
              </Button>
            ) : null}
          </Panel>

          {d.direction === 'revenue' &&
          !['signed', 'completed', 'terminated'].includes(d.status) &&
          can(me.access, 'contracts.sign', { legalEntityId: d.legalEntityId }) ? (
            <Panel title="Подписание">
              <Field label="Дата подписания">
                <Input type="date" value={signDate} onChange={(e) => setSignDate(e.target.value)} />
              </Field>
              <Button
                className="mt-3"
                variant="primary"
                loading={act.isPending}
                onClick={() =>
                  act.mutate({ path: '/sign', body: { signedDate: signDate, version: d.version } })
                }
              >
                Отметить подписание
              </Button>
              <p className="mt-2 text-[12px] text-ink-2">
                Доходный договор с заказчиком подписывает подписант компании или юрлица; маршрут из пяти
                согласующих — для расходных договоров. Подписанный договор с суммой открывает проект для
                заявок и расходных договоров (ПФ-23).
              </p>
            </Panel>
          ) : null}

          {d.direction !== 'revenue' &&
          canSign &&
          !['signed', 'completed', 'terminated'].includes(d.status) ? (
            <Panel title="Подписание">
              <Field label="Дата подписания">
                <Input type="date" value={signDate} onChange={(e) => setSignDate(e.target.value)} />
              </Field>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  disabled={d.status !== 'approved'}
                  loading={act.isPending}
                  onClick={() =>
                    act.mutate({ path: '/sign', body: { signedDate: signDate, version: d.version } })
                  }
                >
                  Отметить подписание
                </Button>
                {d.status !== 'approved' ? (
                  <Button
                    variant="danger"
                    onClick={() =>
                      act.mutate({
                        path: '/sign',
                        body: { signedDate: signDate, bypass: true, version: d.version },
                      })
                    }
                  >
                    Загрузить подписанный в обход маршрута
                  </Button>
                ) : null}
              </div>
              <p className="mt-2 text-[12px] text-ink-2">
                Подпись доступна после завершения маршрута. Договор, подписанный в обход, отмечается, и портал
                готовит служебную записку учредителям (ДГ-16).
              </p>
            </Panel>
          ) : null}

          {d.status === 'signed' ? (
            <Panel title="После подписания">
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-[14px] text-ink">
                    Карточка договора поставщика (приказ № 29П, приложение 3):{' '}
                    {d.cardConfirmedAt
                      ? `подтверждена ${dateTime(d.cardConfirmedAt)}, ${d.cardConfirmedBy ?? ''}`
                      : 'не подтверждена'}
                  </p>
                  {!d.cardConfirmedAt && canConfirmCard ? (
                    <Button
                      className="mt-2"
                      size="sm"
                      variant="primary"
                      onClick={() => act.mutate({ path: '/card-confirm' })}
                    >
                      Подтвердить карточку
                    </Button>
                  ) : null}
                </div>
                <div>
                  <p className="text-[14px] text-ink">
                    Регистрация в 1С:{' '}
                    {d.oneCNumber ? `№ ${d.oneCNumber} от ${date(d.oneCRegisteredAt)}` : 'не зарегистрирован'}
                  </p>
                  {!d.oneCNumber && canOneC ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Input
                        className="w-40"
                        placeholder="номер в 1С"
                        value={oneC.number}
                        onChange={(e) => setOneC({ ...oneC, number: e.target.value })}
                      />
                      <Input
                        className="w-44"
                        type="date"
                        value={oneC.registeredAt}
                        onChange={(e) => setOneC({ ...oneC, registeredAt: e.target.value })}
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={!oneC.number}
                        onClick={() => act.mutate({ path: '/one-c', body: oneC })}
                      >
                        Сохранить
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            </Panel>
          ) : null}
        </div>
      </div>
    </Page>
  );
}
